import { useState, useCallback } from 'react';

// モーダルのタイプ定義
export type ModalType =
  | 'bgm'
  | 'dress'
  | 'appInfo'
  | 'systemSettings'
  | 'restSettings'
  | 'stats'
  | 'confirm';

// 確認ダイアログ用のデータ
interface ConfirmData {
  message: string;
  onConfirm: () => void;
}

export function useModal() {
  const [openModals, setOpenModals] = useState<Set<ModalType>>(new Set());
  const [confirmData, setConfirmData] = useState<ConfirmData | null>(null);

  // モーダルを開く
  const openModal = useCallback((modalType: ModalType) => {
    setOpenModals(prev => new Set(prev).add(modalType));
  }, []);

  // モーダルを閉じる
  const closeModal = useCallback((modalType: ModalType) => {
    setOpenModals(prev => {
      const next = new Set(prev);
      next.delete(modalType);
      return next;
    });
    
    // 確認ダイアログの場合はデータもクリア
    if (modalType === 'confirm') {
      setConfirmData(null);
    }
  }, []);

  // すべてのモーダルを閉じる
  const closeAllModals = useCallback(() => {
    setOpenModals(new Set());
    setConfirmData(null);
  }, []);

  // 確認ダイアログを開く
  const openConfirm = useCallback((message: string, onConfirm: () => void) => {
    setConfirmData({ message, onConfirm });
    openModal('confirm');
  }, [openModal]);

  // モーダルが開いているかチェック
  const isOpen = useCallback((modalType: ModalType) => {
    return openModals.has(modalType);
  }, [openModals]);

  return {
    openModal,
    closeModal,
    closeAllModals,
    openConfirm,
    isOpen,
    confirmData,
    // 便利なショートカット
    modals: {
      bgm: isOpen('bgm'),
      dress: isOpen('dress'),
      appInfo: isOpen('appInfo'),
      systemSettings: isOpen('systemSettings'),
      restSettings: isOpen('restSettings'),
      stats: isOpen('stats'),
      confirm: isOpen('confirm'),
    }
  };
}